import { CommonModule } from '@angular/common';
import { Component, ElementRef, computed, effect, inject, signal, viewChild, viewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BatteryCharging, LucideAngularModule } from 'lucide-angular';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import {
  BatteryConfiguration,
  BessForecastAsset,
  BessForecastBlock,
  BessForecastResponse,
  BessSchedule,
  BessSocLog,
  McpReport,
  McpReportDetail,
} from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { McpReportsService } from '../../mcp-reports/services/mcp-reports.service';
import { BatteryConfigurationsService } from '../services/battery-configurations.service';
import { BessOperationsService } from '../services/bess-operations.service';

type ProductType = 'INTRADAY' | 'DAY-AHEAD';
type MarketProduct = 'DAM' | 'GDAM' | 'RTM';
type DisplayToggle = 'soc' | 'power' | 'bidStatus';

type PlantCell = {
  block: number;
  label: string;
  soc: number | null;
  power: number | null;
  state: 'charging' | 'discharging' | 'idle' | 'bid';
  bidPlaced: boolean;
  source: 'forecast' | 'actual';
};

type PlantRow = {
  entryId: string;
  powerMw: number;
  soc: number;
  chargeLabel: string;
  revenueLabel: string;
  profitLabel: string;
  cells: PlantCell[];
};

@Component({
  standalone: true,
  selector: 'app-battery-storage-system-page',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './battery-storage-system.page.html',
  styleUrl: './battery-storage-system.page.scss',
})
export class BatteryStorageSystemPage {
  private static readonly SERIAL_COLUMN_WIDTH = 64;
  private static readonly PLANT_COLUMN_WIDTH = 196;

  private readonly batteryService = inject(BatteryConfigurationsService);
  private readonly operationsService = inject(BessOperationsService);
  private readonly mcpReportsService = inject(McpReportsService);
  private readonly toast = inject(ToastService);

  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly loading = signal(false);
  protected readonly product = signal<ProductType>('INTRADAY');
  protected readonly threshold = signal('');
  protected readonly displaySoc = signal(true);
  protected readonly displayPower = signal(false);
  protected readonly displayBidStatus = signal(true);
  protected readonly assets = signal<BatteryConfiguration[]>([]);
  protected readonly schedules = signal<BessSchedule[]>([]);
  protected readonly socLogs = signal<BessSocLog[]>([]);
  protected readonly forecasts = signal<BessForecastResponse | null>(null);
  protected readonly marketPrices = signal<Record<MarketProduct, number[]>>({ DAM: [], GDAM: [], RTM: [] });
  private readonly filtersVersion = signal(0);
  private readonly scrollRequestVersion = signal(0);
  private selectedDate = this.resolveSelectedDate('INTRADAY');
  private readonly matrixScroll = viewChild<ElementRef<HTMLDivElement>>('matrixScroll');
  private readonly timeBlockHeaders = viewChildren<ElementRef<HTMLElement>>('timeBlockHeader');

  protected readonly totalBlocks = 96;
  protected readonly hourGroups = Array.from({ length: 24 }, (_, hour) => `${String(hour).padStart(2, '0')}:00`);
  protected readonly quarterLabels = Array.from({ length: 24 }, () => ['00', '15', '30', '45']).flat();
  protected readonly currentBlockIndex = computed(() => this.resolveCurrentBlock());
  protected readonly currentBlock = computed(() => this.blockLabel(this.currentBlockIndex()));
  private readonly thresholdValue = computed(() => {
    this.filtersVersion();
    const value = Number(this.threshold());
    return Number.isFinite(value) && value > 0 ? value : null;
  });

  protected readonly marketPriceRows = computed(() => {
    this.filtersVersion();
    return (['DAM', 'GDAM', 'RTM'] as const).map((market) => ({
      market,
      values: Array.from({ length: 96 }, (_, index) => {
        const value = this.bandValue(market, index + 1);
        return value.toFixed(2).replace(/\.00$/, '');
      }),
    }));
  });

  protected readonly plants = computed<PlantRow[]>(() => {
    this.filtersVersion();
    const selectedDate = this.selectedDate;
    const threshold = this.thresholdValue();
    const forecastProduct = this.selectedForecastProduct();
    const forecastAssets = new Map<string, BessForecastAsset>(
      (this.forecasts()?.products?.[forecastProduct] || []).map((asset) => [asset.battery_configuration_id, asset]),
    );
    const schedulesByAsset = new Map<string, Map<number, BessSchedule>>();
    const logsByAsset = new Map<string, Map<number, BessSocLog>>();

    for (const schedule of this.schedules().filter((row) => row.schedule_date === selectedDate)) {
      const blockMap = schedulesByAsset.get(schedule.battery_configuration_id) || new Map<number, BessSchedule>();
      blockMap.set(schedule.time_block, schedule);
      schedulesByAsset.set(schedule.battery_configuration_id, blockMap);
    }

    for (const log of this.socLogs().filter((row) => row.log_date === selectedDate)) {
      const blockMap = logsByAsset.get(log.battery_configuration_id) || new Map<number, BessSocLog>();
      blockMap.set(log.time_block, log);
      logsByAsset.set(log.battery_configuration_id, blockMap);
    }

    return this.assets().map((asset, assetIndex) => {
      const forecastAsset = forecastAssets.get(asset.id);
      const forecastBlocks = new Map<number, BessForecastBlock>((forecastAsset?.blocks || []).map((block) => [block.block_number, block]));
      const scheduleMap = schedulesByAsset.get(asset.id) || new Map<number, BessSchedule>();
      const logMap = logsByAsset.get(asset.id) || new Map<number, BessSocLog>();
      const powerMw = this.toNumber(asset.power_capacity_mw);
      const energyCapacity = this.toNumber(asset.energy_capacity_mw) || 1;
      let rollingSoc = forecastAsset ? this.toNumber(forecastAsset.starting_soc) : this.findInitialSoc(asset, logMap);

      const cells = Array.from({ length: 96 }, (_, index) => {
        const block = index + 1;
        const forecastBlock = forecastBlocks.get(block);
        const schedule = scheduleMap.get(block);
        const log = logMap.get(block);
        const marketPrice = this.bandValue(forecastProduct, block);

        if (forecastBlock) {
          rollingSoc = this.toNumber(forecastBlock.forecast_soc) || rollingSoc;
        } else if (log) {
          rollingSoc = this.toNumber(log.soc_after) || rollingSoc;
        } else if (schedule) {
          const deltaPercent = (this.toNumber(schedule.energy_mw) / energyCapacity) * 25;
          if (schedule.action === 'charge') {
            rollingSoc = this.clampSoc(rollingSoc + deltaPercent, asset);
          } else if (schedule.action === 'discharge') {
            rollingSoc = this.clampSoc(rollingSoc - deltaPercent, asset);
          } else {
            rollingSoc = this.clampSoc(rollingSoc, asset);
          }
        }

        const action = forecastBlock ? this.normalizeAction(forecastBlock.action) : this.normalizeAction(schedule?.action);
        let bidPlaced = forecastBlock ? action !== 'idle' : !!schedule && (schedule.status || '').toLowerCase() !== 'planned';
        if (!forecastBlock && !schedule && threshold !== null && marketPrice < threshold) {
          bidPlaced = false;
        }
        const power = forecastBlock
          ? this.toNumber(forecastBlock.forecast_mw)
          : schedule
            ? this.toNumber(schedule.energy_mw)
            : action === 'idle'
              ? null
              : Math.round(powerMw * 0.34);
        const state: PlantCell['state'] =
          action === 'charge' ? 'charging' : action === 'discharge' ? 'discharging' : bidPlaced ? 'bid' : 'idle';

        return {
          block,
          label: this.blockLabel(block),
          soc: forecastBlock ? this.toNumber(forecastBlock.forecast_soc) : Number(rollingSoc.toFixed(2)),
          power,
          state,
          bidPlaced,
          source: forecastBlock ? ('forecast' as const) : ('actual' as const),
        };
      });

      const currentCell = cells[Math.max(0, this.currentBlockIndex() - 1)];
      const totalChargeMw = cells.reduce((sum, cell) => sum + (cell.state === 'charging' ? Math.abs(cell.power || 0) : 0), 0);
      const totalRevenue = cells.reduce((sum, cell, index) => sum + this.bandValue('RTM', index + 1) * Math.abs(cell.power || 0), 0) / 100;
      const totalProfit = totalRevenue * 0.54;

      return {
        entryId: asset.entry_id,
        powerMw,
        soc: currentCell?.soc || rollingSoc,
        chargeLabel: `${totalChargeMw.toFixed(1)} MW`,
        revenueLabel: `${totalRevenue.toFixed(1)} L`,
        profitLabel: `${totalProfit.toFixed(1)} L`,
        cells,
      };
    });
  });

  constructor() {
    effect(() => {
      this.scrollRequestVersion();
      const scrollHost = this.matrixScroll()?.nativeElement;
      if (!scrollHost) {
        return;
      }
      if (this.product() !== 'INTRADAY') {
        queueMicrotask(() => scrollHost.scrollTo({ left: 0, behavior: 'auto' }));
        return;
      }
      const target = this.timeBlockHeaders()[this.currentBlockIndex() - 1]?.nativeElement;
      if (!target) {
        return;
      }
      queueMicrotask(() => this.scrollToCurrentBlock(scrollHost, target));
    });
    this.load();
  }

  protected setProduct(value: string): void {
    const nextProduct: ProductType = value === 'DAY-AHEAD' ? 'DAY-AHEAD' : 'INTRADAY';
    this.product.set(nextProduct);
    this.selectedDate = this.resolveSelectedDate(nextProduct);
    this.filtersVersion.update((current) => current + 1);
    this.loadForecasts();
  }

  protected setThreshold(value: string): void {
    this.threshold.set((value || '').toString());
    this.filtersVersion.update((current) => current + 1);
  }

  protected setToggle(toggle: DisplayToggle, value: boolean): void {
    if (toggle === 'soc') {
      this.displaySoc.set(!!value);
    } else if (toggle === 'power') {
      this.displayPower.set(!!value);
    } else {
      this.displayBidStatus.set(!!value);
    }
  }

  protected trackByEntry(_: number, row: PlantRow): string {
    return row.entryId;
  }

  private load(): void {
    this.loading.set(true);
    this.batteryService.list().subscribe({
      next: (assets) => {
        this.assets.set(assets);
        this.operationsService.listSchedules().subscribe({
          next: (schedules) => {
            this.schedules.set(schedules);
            this.operationsService.listSocLogs().subscribe({
              next: (logs) => {
                this.socLogs.set(logs);
                this.loadForecasts();
              },
              error: () => {
                this.socLogs.set([]);
                this.toast.show('Failed to load BESS SOC logs', '', 'danger');
                this.loadForecasts();
              },
            });
          },
          error: () => {
            this.schedules.set([]);
            this.socLogs.set([]);
            this.loading.set(false);
            this.toast.show('Failed to load BESS schedules', '', 'danger');
          },
        });
      },
      error: () => {
        this.assets.set([]);
        this.schedules.set([]);
        this.socLogs.set([]);
        this.forecasts.set(null);
        this.loading.set(false);
        this.toast.show('Failed to load battery assets', '', 'danger');
      },
    });
  }

  private loadForecasts(): void {
    this.operationsService.listForecasts(this.selectedForecastProduct(), this.selectedDate).subscribe({
      next: (response) => {
        this.forecasts.set(response);
        if (response.forecast_date) {
          this.selectedDate = response.forecast_date;
        }
        this.loadMarketPrices(this.selectedDate);
        this.loading.set(false);
        this.filtersVersion.update((current) => current + 1);
        this.requestCurrentBlockScroll();
      },
      error: () => {
        this.forecasts.set(null);
        this.loadMarketPrices(this.selectedDate);
        this.loading.set(false);
        this.requestCurrentBlockScroll();
        this.toast.show('Failed to load BESS forecasts', '', 'danger');
      },
    });
  }

  private loadMarketPrices(selectedDate: string): void {
    const targetDate = this.toReportDate(selectedDate);
    const products: MarketProduct[] = ['DAM', 'GDAM', 'RTM'];
    forkJoin(
      products.map((product) =>
        this.mcpReportsService.list(product).pipe(
          map((reports) => this.pickMatchingReport(reports, targetDate)),
          switchMap((report) => (report ? this.mcpReportsService.get(report.id) : of(null))),
          map((detail) => [product, this.buildPriceSeries(product, detail, targetDate)] as const),
          catchError(() => of([product, Array.from({ length: 96 }, (_, index) => this.syntheticBandValue(product, index + 1))] as const)),
        ),
      ),
    ).subscribe({
      next: (entries) => {
        this.marketPrices.set({
          DAM: entries.find(([product]) => product === 'DAM')?.[1] || [],
          GDAM: entries.find(([product]) => product === 'GDAM')?.[1] || [],
          RTM: entries.find(([product]) => product === 'RTM')?.[1] || [],
        });
        this.filtersVersion.update((current) => current + 1);
      },
    });
  }

  private selectedForecastProduct(): 'DAM' | 'RTM' {
    return this.product() === 'DAY-AHEAD' ? 'DAM' : 'RTM';
  }

  private resolveSelectedDate(product: ProductType): string {
    const dateValue = new Date();
    if (product === 'DAY-AHEAD') {
      dateValue.setDate(dateValue.getDate() + 1);
    }
    return dateValue.toISOString().slice(0, 10);
  }

  private resolveCurrentBlock(): number {
    const now = new Date();
    const minutes = now.getHours() * 60 + now.getMinutes();
    return Math.min(96, Math.max(1, Math.floor(minutes / 15) + 1));
  }

  private blockLabel(block: number): string {
    const minutes = (block - 1) * 15;
    const startHour = Math.floor(minutes / 60);
    const startMinute = minutes % 60;
    const endMinutes = minutes + 15;
    const endHour = Math.floor(endMinutes / 60) % 24;
    const endMinute = endMinutes % 60;
    return `${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}-${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
  }

  private bandValue(product: MarketProduct, block: number): number {
    const liveValues = this.marketPrices()[product];
    const liveValue = liveValues?.[block - 1];
    if (Number.isFinite(liveValue)) {
      return Number(liveValue);
    }
    return this.syntheticBandValue(product, block);
  }

  private syntheticBandValue(product: MarketProduct, block: number): number {
    const hour = Math.floor((block - 1) / 4);
    const quarter = (block - 1) % 4;
    const middayBump = hour >= 7 && hour <= 9 ? 5.2 : hour >= 18 && hour <= 21 ? 2.1 : 0;
    const base = this.product() === 'INTRADAY' ? 2.05 : 2.55;
    const bandOffset = product === 'DAM' ? 0.06 : product === 'GDAM' ? 0.12 : 0;
    const pattern = [0.0, 0.18, 0.4, 0.28][quarter];
    return Number((base + bandOffset + (hour * 0.19) % 2.8 + middayBump + pattern).toFixed(2));
  }

  private pickMatchingReport(reports: McpReport[], targetDate: string): McpReport | null {
    return reports.find((report) => report.report_from_date === targetDate && report.report_to_date === targetDate) || reports[0] || null;
  }

  private buildPriceSeries(product: MarketProduct, detail: McpReportDetail | null, targetDate: string): number[] {
    const fallback = Array.from({ length: 96 }, (_, index) => this.syntheticBandValue(product, index + 1));
    if (!detail) {
      return fallback;
    }

    const byLabel = new Map<string, number>();
    const byBlock = new Map<number, number>();
    const siteRows = this.extractSiteRows(detail);

    for (const row of siteRows) {
      if (row.date && row.date !== targetDate) {
        continue;
      }
      const value = this.toNumber(row.mcp) / 1000;
      if (!Number.isFinite(value) || value <= 0) {
        continue;
      }
      const normalizedPeriod = this.normalizePeriodLabel(row.period);
      if (normalizedPeriod) {
        byLabel.set(normalizedPeriod, value);
      }
      const blockNumber = this.parsePeriodBlock(row.period) ?? row.block;
      if (blockNumber !== null) {
        byBlock.set(blockNumber, value);
      }
    }

    if (byLabel.size === 0 && byBlock.size === 0) {
      const matchingDateRows = detail.rows.filter((row) => !row.report_date || row.report_date === targetDate);
      const sourceRows = matchingDateRows.length > 0 ? matchingDateRows : detail.rows;
      for (const row of sourceRows) {
        const value = this.toNumber(row.mcp) / 1000;
        if (!Number.isFinite(value) || value <= 0) {
          continue;
        }
        const normalizedPeriod = this.normalizePeriodLabel(row.period);
        if (normalizedPeriod) {
          byLabel.set(normalizedPeriod, value);
        }
        const blockNumber = this.parsePeriodBlock(row.period);
        if (blockNumber !== null) {
          byBlock.set(blockNumber, value);
        } else if (row.row_order >= 1 && row.row_order <= 96) {
          byBlock.set(row.row_order, value);
        }
      }
    }

    return Array.from({ length: 96 }, (_, index) => {
      const block = index + 1;
      return byBlock.get(block) ?? byLabel.get(this.normalizePeriodLabel(this.blockLabel(block))) ?? fallback[index];
    });
  }

  private extractSiteRows(detail: McpReportDetail): Array<{ date: string; period: string; mcp: number | string | null; block: number | null }> {
    let parsed: unknown;
    try {
      parsed = JSON.parse(detail.summary_json || '{}');
    } catch {
      return [];
    }
    if (!parsed || typeof parsed !== 'object' || !Array.isArray((parsed as { site_rows?: unknown[] }).site_rows)) {
      return [];
    }

    return ((parsed as { site_rows: Array<Record<string, unknown>> }).site_rows || []).map((row) => {
      const normalized = new Map<string, unknown>();
      for (const [key, value] of Object.entries(row)) {
        normalized.set(key.replace(/[^a-z0-9]+/gi, '').toLowerCase(), value);
      }

      const period = String(normalized.get('period') ?? normalized.get('timeblock') ?? normalized.get('timeslot') ?? '');
      const blockCandidate = normalized.get('hour') ?? normalized.get('block') ?? normalized.get('timeblockno');
      const block =
        typeof blockCandidate === 'number'
          ? blockCandidate
          : /^\d+$/.test(String(blockCandidate ?? '').trim())
            ? Number(String(blockCandidate).trim())
            : null;

      return {
        date: String(normalized.get('date') ?? normalized.get('deliverydate') ?? normalized.get('tradingdate') ?? ''),
        period,
        mcp: (normalized.get('mcp') ?? normalized.get('mcprsmwh') ?? normalized.get('mcprsmwh)') ?? null) as number | string | null,
        block,
      };
    });
  }

  private normalizePeriodLabel(value: string | null | undefined): string {
    return (value || '').replace(/\s+/g, '').toUpperCase();
  }

  private parsePeriodBlock(value: string | null | undefined): number | null {
    const normalized = this.normalizePeriodLabel(value);
    if (!/^\d{1,3}$/.test(normalized)) {
      return null;
    }
    const block = Number(normalized);
    return Number.isInteger(block) && block >= 1 && block <= 96 ? block : null;
  }

  private toReportDate(value: string): string {
    const [year, month, day] = (value || '').split('-');
    if (!year || !month || !day) {
      return value;
    }
    return `${day}-${month}-${year}`;
  }

  private findInitialSoc(asset: BatteryConfiguration, logMap: Map<number, BessSocLog>): number {
    const orderedLogs = [...logMap.values()].sort((left, right) => left.time_block - right.time_block);
    if (orderedLogs.length > 0) {
      return this.toNumber(orderedLogs[0].soc_before) || this.toNumber(asset.soc_initial) || 0;
    }
    return this.toNumber(asset.soc_initial) || 0;
  }

  private clampSoc(value: number, asset: BatteryConfiguration): number {
    const lower = this.toNumber(asset.soc_final);
    return Math.max(lower, Math.min(100, value));
  }

  private normalizeAction(action: string | undefined): 'charge' | 'discharge' | 'idle' {
    const normalized = (action || '').toLowerCase();
    if (normalized === 'charge') return 'charge';
    if (normalized === 'discharge') return 'discharge';
    return 'idle';
  }

  private toNumber(value: string | number | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private requestCurrentBlockScroll(): void {
    this.scrollRequestVersion.update((current) => current + 1);
  }

  private scrollToCurrentBlock(scrollHost: HTMLDivElement, target: HTMLElement): void {
    const stickyOffset = BatteryStorageSystemPage.SERIAL_COLUMN_WIDTH + BatteryStorageSystemPage.PLANT_COLUMN_WIDTH;
    const nextLeft = Math.max(0, target.offsetLeft - stickyOffset);
    scrollHost.scrollTo({ left: nextLeft, behavior: 'smooth' });
  }
}
